import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { CogIcon, CalculatorIcon } from '@heroicons/react/24/outline';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Production = () => {
  const [mode, setMode] = useState('product'); // 'product' or 'recipe'
  const [products, setProducts] = useState([]);
  const [recipes, setRecipes] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState('');
  const [selectedRecipe, setSelectedRecipe] = useState('');
  const [bottleFormat, setBottleFormat] = useState('');
  const [bottles, setBottles] = useState('');
  const [productionData, setProductionData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadProducts();
    loadRecipes();
  }, []);

  const loadProducts = async () => {
    try {
      const response = await axios.get(`${API}/products`);
      setProducts(response.data);
    } catch (error) {
      console.error('Errore nel caricamento dei prodotti:', error);
      toast.error('Errore nel caricamento dei prodotti');
    }
  };

  const loadRecipes = async () => {
    try {
      const response = await axios.get(`${API}/recipes`);
      setRecipes(response.data);
    } catch (error) {
      console.error('Errore nel caricamento delle ricette:', error);
      toast.error('Errore nel caricamento delle ricette');
    }
  };

  const calculateProduction = async () => {
    // Validazione in base alla modalità
    if (mode === 'product') {
      if (!selectedProduct || !bottles || parseInt(bottles) <= 0) {
        toast.error('Seleziona un prodotto e inserisci un numero valido di bottiglie');
        return;
      }
    } else {
      if (!selectedRecipe || !bottleFormat || !bottles || parseInt(bottles) <= 0) {
        toast.error('Seleziona una ricetta, formato bottiglia e numero bottiglie');
        return;
      }
    }

    try {
      setLoading(true);
      const params = {
        bottles: parseInt(bottles)
      };
      
      if (mode === 'product') {
        params.product_id = selectedProduct;
      } else {
        params.recipe_id = selectedRecipe;
        params.bottle_format = parseInt(bottleFormat);
      }
      
      const response = await axios.get(`${API}/production/calculate`, { params });
      setProductionData(response.data);
    } catch (error) {
      console.error('Errore nel calcolo produzione:', error);
      toast.error(error.response?.data?.detail || 'Errore nel calcolo della produzione');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('it-IT', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 3
    }).format(value);
  };

  const selectedProductData = products.find(p => p.id === selectedProduct);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Calcolo Produzione</h1>
        <p className="text-gray-600">Calcola ingredienti e costi necessari per produrre un numero specifico di bottiglie</p>
      </div>

      {/* Input Form */}
      <Card>
        <CardHeader>
          <CardTitle>Parametri Produzione</CardTitle>
          <CardDescription>Scegli la modalità di calcolo e inserisci i parametri</CardDescription>
        </CardHeader>
        <CardContent>
          {/* Mode Selection */}
          <div className="mb-6">
            <Label className="mb-3 block">Modalità di Calcolo</Label>
            <div className="flex gap-4">
              <button
                onClick={() => {
                  setMode('product');
                  setProductionData(null);
                }}
                className={`flex-1 p-4 border-2 rounded-lg transition-all ${
                  mode === 'product'
                    ? 'border-blue-500 bg-blue-50 text-blue-700'
                    : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'
                }`}
              >
                <div className="font-medium mb-1">Da Prodotto</div>
                <div className="text-xs">Include ingredienti + packaging</div>
              </button>
              <button
                onClick={() => {
                  setMode('recipe');
                  setProductionData(null);
                }}
                className={`flex-1 p-4 border-2 rounded-lg transition-all ${
                  mode === 'recipe'
                    ? 'border-blue-500 bg-blue-50 text-blue-700'
                    : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'
                }`}
              >
                <div className="font-medium mb-1">Da Ricetta</div>
                <div className="text-xs">Solo ingredienti, formato personalizzato</div>
              </button>
            </div>
          </div>

          {/* Dynamic Form based on mode */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {mode === 'product' ? (
              <>
                <div className="space-y-2 md:col-span-2">
                  <Label htmlFor="product">Prodotto *</Label>
                  <Select value={selectedProduct} onValueChange={setSelectedProduct}>
                    <SelectTrigger data-testid="product-select">
                      <SelectValue placeholder="Seleziona prodotto" />
                    </SelectTrigger>
                    <SelectContent>
                      {products.map((product) => (
                        <SelectItem key={product.id} value={product.id}>
                          {product.name} ({product.code})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {selectedProductData && (
                    <p className="text-xs text-gray-500">
                      Formato: {selectedProductData.bottle_format}ml • {selectedProductData.bottles_per_batch?.toFixed(1)} bottiglie per batch
                    </p>
                  )}
                </div>
              </>
            ) : (
              <>
                <div className="space-y-2 md:col-span-2">
                  <Label htmlFor="recipe">Ricetta *</Label>
                  <Select value={selectedRecipe} onValueChange={setSelectedRecipe}>
                    <SelectTrigger data-testid="recipe-select">
                      <SelectValue placeholder="Seleziona ricetta" />
                    </SelectTrigger>
                    <SelectContent>
                      {recipes.map((recipe) => (
                        <SelectItem key={recipe.id} value={recipe.id}>
                          {recipe.name} ({recipe.target_volume}L - {recipe.target_alcohol_degree}°)
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="format">Formato (ml) *</Label>
                  <Input
                    id="format"
                    type="number"
                    min="1"
                    value={bottleFormat}
                    onChange={(e) => setBottleFormat(e.target.value)}
                    placeholder="es. 200, 500, 700"
                    data-testid="format-input"
                  />
                </div>
              </>
            )}

            <div className="space-y-2">
              <Label htmlFor="bottles">Numero Bottiglie *</Label>
              <Input
                id="bottles"
                type="number"
                min="1"
                value={bottles}
                onChange={(e) => setBottles(e.target.value)}
                placeholder="es. 1000"
                data-testid="bottles-input"
              />
            </div>

            <div className="flex items-end">
              <Button 
                onClick={calculateProduction} 
                disabled={
                  loading || 
                  !bottles ||
                  (mode === 'product' && !selectedProduct) ||
                  (mode === 'recipe' && (!selectedRecipe || !bottleFormat))
                }
                className="w-full"
                data-testid="calculate-btn"
              >
                <CalculatorIcon className="w-4 h-4 mr-2" />
                {loading ? 'Calcolo...' : 'Calcola Produzione'}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Results */}
      {productionData && (
        <div className="space-y-4">
          {/* Summary Card */}
          <Card className="bg-blue-50 border-blue-200">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CogIcon className="w-5 h-5" />
                Riepilogo Produzione
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <p className="text-sm text-gray-600">Prodotto</p>
                  <p className="text-lg font-bold">{productionData.product_name}</p>
                  <p className="text-xs text-gray-500">{productionData.product_code}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Bottiglie da Produrre</p>
                  <p className="text-lg font-bold">{productionData.bottles_requested.toLocaleString()}</p>
                  <p className="text-xs text-gray-500">{productionData.bottle_format}ml</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Batch Necessari</p>
                  <p className="text-lg font-bold">{productionData.batches_needed}</p>
                  <p className="text-xs text-gray-500">{productionData.recipe_volume}L per batch</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Costo per Bottiglia</p>
                  <p className="text-lg font-bold text-green-600">{formatCurrency(productionData.costs.cost_per_bottle)}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Ingredients Table */}
          <Card>
            <CardHeader>
              <div className="flex justify-between items-center">
                <div>
                  <CardTitle>Ingredienti Necessari</CardTitle>
                  <CardDescription>Quantità per {productionData.batches_needed} batch</CardDescription>
                </div>
                <Badge variant="outline" className="text-lg">
                  Totale: {formatCurrency(productionData.costs.total_ingredients)}
                </Badge>
              </div>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 border-b">
                    <tr>
                      <th className="px-4 py-3 text-left font-medium text-gray-900">Ingrediente</th>
                      <th className="px-4 py-3 text-right font-medium text-gray-900">Quantità</th>
                      <th className="px-4 py-3 text-right font-medium text-gray-900">Unità</th>
                      <th className="px-4 py-3 text-right font-medium text-gray-900">Costo Unitario</th>
                      <th className="px-4 py-3 text-right font-medium text-gray-900">Costo Totale</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {productionData.ingredients.map((ingredient, index) => (
                      <tr key={index} className="hover:bg-gray-50">
                        <td className="px-4 py-3">
                          <div className="font-medium text-gray-900">{ingredient.ingredient_name}</div>
                          <div className="text-xs text-gray-500">{ingredient.ingredient_code}</div>
                        </td>
                        <td className="px-4 py-3 text-right font-medium">{ingredient.quantity.toLocaleString()}</td>
                        <td className="px-4 py-3 text-right text-gray-600">{ingredient.unit_of_measure}</td>
                        <td className="px-4 py-3 text-right text-gray-600">{formatCurrency(ingredient.cost_per_unit)}</td>
                        <td className="px-4 py-3 text-right font-bold text-green-600">{formatCurrency(ingredient.total_cost)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>

          {/* Packaging Table - Only for product mode */}
          {productionData.mode === 'product' && productionData.packaging.length > 0 && (
            <Card>
              <CardHeader>
                <div className="flex justify-between items-center">
                  <div>
                    <CardTitle>Packaging Necessario</CardTitle>
                    <CardDescription>Componenti per {productionData.bottles_requested.toLocaleString()} bottiglie</CardDescription>
                  </div>
                  <Badge variant="outline" className="text-lg">
                    Totale: {formatCurrency(productionData.costs.total_packaging)}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 border-b">
                      <tr>
                        <th className="px-4 py-3 text-left font-medium text-gray-900">Componente</th>
                        <th className="px-4 py-3 text-right font-medium text-gray-900">Quantità</th>
                        <th className="px-4 py-3 text-right font-medium text-gray-900">Unità</th>
                        <th className="px-4 py-3 text-right font-medium text-gray-900">Costo Unitario</th>
                        <th className="px-4 py-3 text-right font-medium text-gray-900">Costo Totale</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {productionData.packaging.map((pkg, index) => (
                        <tr key={index} className="hover:bg-gray-50">
                          <td className="px-4 py-3">
                            <div className="font-medium text-gray-900">{pkg.component_name}</div>
                            <div className="text-xs text-gray-500">{pkg.component_type}</div>
                          </td>
                          <td className="px-4 py-3 text-right font-medium">{pkg.quantity.toLocaleString()}</td>
                          <td className="px-4 py-3 text-right text-gray-600">{pkg.unit_of_measure}</td>
                          <td className="px-4 py-3 text-right text-gray-600">{formatCurrency(pkg.cost_per_unit)}</td>
                          <td className="px-4 py-3 text-right font-bold text-green-600">{formatCurrency(pkg.total_cost)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Total Cost Card */}
          <Card className="bg-green-50 border-green-200">
            <CardHeader>
              <CardTitle>Costo Totale Produzione</CardTitle>
            </CardHeader>
            <CardContent>
              <div className={`grid grid-cols-1 ${productionData.mode === 'product' ? 'md:grid-cols-3' : 'md:grid-cols-2'} gap-6`}>
                <div className="text-center p-4 bg-white rounded-lg border">
                  <p className="text-sm text-gray-600 mb-1">Costo Ingredienti</p>
                  <p className="text-2xl font-bold text-blue-600">{formatCurrency(productionData.costs.total_ingredients)}</p>
                </div>
                {productionData.mode === 'product' && (
                  <div className="text-center p-4 bg-white rounded-lg border">
                    <p className="text-sm text-gray-600 mb-1">Costo Packaging</p>
                    <p className="text-2xl font-bold text-purple-600">{formatCurrency(productionData.costs.total_packaging)}</p>
                  </div>
                )}
                <div className="text-center p-4 bg-white rounded-lg border border-green-300">
                  <p className="text-sm text-gray-600 mb-1">Costo Totale</p>
                  <p className="text-3xl font-bold text-green-600">{formatCurrency(productionData.costs.total_production)}</p>
                  <p className="text-xs text-gray-500 mt-1">{formatCurrency(productionData.costs.cost_per_bottle)} per bottiglia</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Empty State */}
      {!productionData && !loading && (
        <Card className="text-center py-16">
          <CardContent>
            <CogIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">Nessun calcolo effettuato</h3>
            <p className="text-sm text-gray-600">
              Seleziona un prodotto e il numero di bottiglie per calcolare gli ingredienti e i costi necessari
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default Production;
